import deepEqual from 'fast-deep-equal'

type Entry = { inputs: unknown[] } & (
  | { status: 'pending'; promise: Promise<void> }
  | { status: 'fulfilled'; response: unknown }
  | { status: 'rejected'; error: unknown }
)

export const createPromiseSuspenseCache = () => {
  let entries: Entry[] = []

  const settle = (pending: Entry, settled: Entry) => {
    entries = entries.map(entry => (entry === pending ? settled : entry))
  }

  const usePromiseSuspense = <Args extends unknown[], Result>(
    load: (...inputs: Args) => Promise<Result>,
    inputs: Args
  ) => {
    const cached = entries.find(entry => deepEqual(entry.inputs, inputs))
    if (cached?.status === 'fulfilled') {
      // Entries are keyed by inputs alone, so the cache cannot carry each caller's Result type.
      return cached.response as Result
    }
    if (cached?.status === 'rejected') {
      throw cached.error
    }
    if (cached) {
      throw cached.promise
    }

    const promise = load(...inputs).then(
      response => settle(pending, { inputs, status: 'fulfilled', response }),
      (error: unknown) => settle(pending, { inputs, status: 'rejected', error })
    )
    const pending: Entry = { inputs, status: 'pending', promise }
    entries = [...entries, pending]
    throw promise
  }

  const clear = () => {
    entries = []
  }

  return { usePromiseSuspense, clear }
}

export const { usePromiseSuspense } = createPromiseSuspenseCache()
